import { useEffect, useState } from "react";
import { createPartnerApplication } from "../../api/partnerApplications";
import { IPartnerApplicationCreated } from "../../models/IPartnerApplication";
import { Alert, Button, Dialog, Field, Input, Textarea } from "../../ui";
import "./styles.scss";

type PartnerApplicationDialogProps = {
  isOpen: boolean;
  onClose: (created?: IPartnerApplicationCreated) => void;
};

const PartnerApplicationDialog = (props: PartnerApplicationDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    contactEmail: "",
  });

  useEffect(() => {
    if (props.isOpen) {
      setFormData({ name: "", description: "", contactEmail: "" });
    }
  }, [props.isOpen]);

  const handleClose = (created?: IPartnerApplicationCreated) => {
    props.onClose(created);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setErrorMessage("Application name is required");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const { data } = await createPartnerApplication({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        contactEmail: formData.contactEmail.trim() || undefined,
      });
      handleClose(data);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to create partner application");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      title="New Partner Application"
      busy={loading}
      bodyClassName="partner-app-dialog__body"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            Create & Issue Key
          </Button>
        </>
      }
    >
      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

      <Alert tone="info">
        Issue an API key for a third-party application that wants to reserve courts on behalf of its users.
        The key is shown <strong>once</strong> after creation — copy it immediately.
      </Alert>

      <Field label="Application Name" required>
        {(id) => (
          <Input
            id={id}
            type="text"
            placeholder="e.g., Acme Bookings"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            disabled={loading}
          />
        )}
      </Field>

      <Field label="Description">
        {(id) => (
          <Textarea
            id={id}
            placeholder="What does this partner do? Who owns it?"
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            rows={3}
            disabled={loading}
          />
        )}
      </Field>

      <Field label="Contact Email">
        {(id) => (
          <Input
            id={id}
            type="email"
            placeholder="ops@acme.com"
            value={formData.contactEmail}
            onChange={(e) => handleChange("contactEmail", e.target.value)}
            disabled={loading}
          />
        )}
      </Field>
    </Dialog>
  );
};

export default PartnerApplicationDialog;
